import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  DeleteOutline,
  EditOutlined,
  GridViewRounded,
  Groups2Rounded,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LayersRounded,
  MoreVert,
  ShieldRounded,
} from "@mui/icons-material";
import {
  GlobalSidePanel,
  PanelHeaderIconButton,
} from "components/DesignSystem/GlobalSidePanel";
import { Loader } from "components/Loader";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { customGetAPI } from "services/utils/CustomAPI";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { roleAndGroupUi, statusChip } from "../ui";
import { resolveRoleStatusRecord } from "./roleStatus";
import {
  CountBadge,
  ModuleList,
  PermissionMatrix,
  ProductIcon,
  ReadOnlyCheck,
  RelatedItemsCard,
  SearchField,
  SectionHeading,
  SectionIcon,
  getProductSelectedCount,
  getProductTotalCount,
  getRoleStatus,
  groupProductRows,
  isSubmoduleSelected,
  roleKitType,
  teal,
  usedInCompaniesLabel,
} from "./roleUiKit";

const cardSx = {
  ...roleAndGroupUi.sectionCard,
  p: { xs: 2, md: 2.25 },
};

const readOnlyFieldSx = {
  px: 1.75,
  py: 1.1,
  borderRadius: "10px",
  border: `1px solid ${designColors.slate200}`,
  backgroundColor: designColors.white,
  fontSize: 14,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  minHeight: 42,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const fieldLabelSx = {
  fontSize: 13.5,
  fontWeight: 700,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  mb: 0.75,
};

const Stat = ({ icon, value, label }) => (
  <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
    <SectionIcon icon={icon} size={38} color={teal} bg="#FFFFFF" />
    <Mui.Box>
      <Mui.Typography sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate800, lineHeight: 1.2 }}>
        {value}
      </Mui.Typography>
      <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600 }}>{label}</Mui.Typography>
    </Mui.Box>
  </Mui.Stack>
);

// Shared with "Related User Roles" on View User Group (RelatedItemsCard).
const RelatedUserGroups = ({ groups, onOpenGroup, wide }) => (
  <RelatedItemsCard
    sx={cardSx}
    icon={<Groups2Rounded />}
    iconColor="#9333EA"
    iconBg="#FAF5FF"
    title="Related User Groups"
    subtitle="User groups currently using this role."
    countLabel={`${groups.length} user group${groups.length === 1 ? "" : "s"}`}
    emptyText="No user groups use this role yet."
    items={groups.map((group) => ({
      key: group?.id ?? group?.name,
      name: group?.name,
      description: group?.description,
      meta: usedInCompaniesLabel(group.companyCount),
      data: group,
    }))}
    onOpen={onOpenGroup}
    wide={wide}
  />
);

// Collapsed-drawer module tree: each module expands to its sub-modules with
// read-only checkboxes.
const ModuleTree = ({ products, search }) => {
  const [open, setOpen] = useState({});
  const query = search.trim().toLowerCase();

  const visible = products
    .map((product) => ({ product, groups: groupProductRows(product, search) }))
    .filter(
      ({ product, groups }) =>
        !query || groups.length > 0 || product?.product_name?.toLowerCase().includes(query),
    );

  if (!visible.length) {
    return (
      <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate600, py: 2, textAlign: "center" }}>
        {query ? "No modules or sub-modules match your search." : "This role has no modules yet."}
      </Mui.Typography>
    );
  }

  return (
    <Mui.Stack spacing={1}>
      {visible.map(({ product, groups }, index) => {
        const name = product?.product_name;
        const selected = getProductSelectedCount(product);
        const isOpen = open[name] ?? (query ? true : index === 0);
        return (
          <Mui.Box
            key={name}
            sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: "10px", overflow: "hidden" }}
          >
            <Mui.Box
              role="button"
              tabIndex={0}
              onClick={() => setOpen((prev) => ({ ...prev, [name]: !isOpen }))}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setOpen((prev) => ({ ...prev, [name]: !isOpen }));
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.25,
                py: 0.75,
                cursor: "pointer",
                backgroundColor: isOpen ? "#ECFAF6" : designColors.white,
              }}
            >
              <ReadOnlyCheck checked={selected > 0} />
              <ProductIcon name={name} size={30} />
              <Mui.Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: designColors.slate800 }}>
                {name}
              </Mui.Typography>
              <CountBadge selected={selected} total={getProductTotalCount(product)} />
              {isOpen ? (
                <KeyboardArrowDown sx={{ color: designColors.slate500 }} />
              ) : (
                <KeyboardArrowRight sx={{ color: designColors.slate500 }} />
              )}
            </Mui.Box>
            <Mui.Collapse in={isOpen} unmountOnExit>
              <Mui.Box
                sx={{
                  ml: "30px",
                  pl: 2.5,
                  py: 0.75,
                  borderLeft: `1px dashed ${designColors.slate200}`,
                }}
              >
                {groups.map(({ module, submodules, showHeader }) => (
                  <React.Fragment key={module?.module_name}>
                    {showHeader ? (
                      <Mui.Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: teal, mt: 0.75, mb: 0.25 }}
                      >
                        {module?.module_name}
                      </Mui.Typography>
                    ) : null}
                    {submodules.map((submodule) => (
                      <Mui.Stack
                        key={`${module?.module_name}-${submodule?.sub_module_name}`}
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                      >
                        <ReadOnlyCheck checked={isSubmoduleSelected(submodule)} />
                        <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate700 }}>
                          {submodule?.sub_module_name}
                        </Mui.Typography>
                      </Mui.Stack>
                    ))}
                  </React.Fragment>
                ))}
              </Mui.Box>
            </Mui.Collapse>
          </Mui.Box>
        );
      })}
    </Mui.Stack>
  );
};

export const RoleViewPanel = ({
  open,
  role,
  onClose,
  canEdit,
  onEdit,
  onDelete,
  userGroups = [],
  onOpenUserGroup,
  // Temporarily hide the panel (e.g. while Edit Role is open over it)
  // without closing it: its loaded data, expand state and selection are
  // kept, so it comes back instantly with no refetch or loader.
  hidden = false,
  // Changing it refetches the role (after Edit Role saves).
  refreshKey = 0,
  // The mode Edit Role was last in; applied when the view is shown again.
  restoreExpanded,
}) => {
  // Roles are schema-wide: the role endpoint needs no company_id.
  const { domain } = useSelector((state) => state.tokenReducer);
  const [expanded, setExpanded] = useState(false);
  const [products, setProducts] = useState([]);
  const [roleRecord, setRoleRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeProduct, setActiveProduct] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);

  // While hidden behind Edit, follow the mode Edit is in, so the view comes
  // back already in that mode: collapsing Edit to the side panel and closing
  // it lands on the side panel view (2026-10-01, user request). Syncing only
  // once shown used to flash the old full view first.
  useEffect(() => {
    if (hidden && typeof restoreExpanded === "boolean") setExpanded(restoreExpanded);
  }, [hidden, restoreExpanded]);

  useEffect(() => {
    if (!open) {
      setExpanded(false);
      setSearch("");
      setMenuAnchor(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !role?.role_id) {
      setProducts([]);
      return undefined;
    }
    let cancelled = false;
    setRoleRecord(null);
    setLoading(true);
    customGetAPI(`${domain}product-control/role/?role_id=${role.role_id}`)
      .then((res) => {
        if (cancelled) return;
        const next = res?.responseDetails?.products ?? [];
        setRoleRecord(res?.responseDetails ?? null);
        setProducts(next);
        const first = next.find((product) => getProductSelectedCount(product) > 0) || next[0];
        setActiveProduct(first?.product_name ?? "");
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain, open, role?.role_id, refreshKey]);

  const stats = useMemo(
    () => ({
      modules: products.filter((product) => getProductSelectedCount(product) > 0).length,
      submodules: products.reduce((total, product) => total + getProductSelectedCount(product), 0),
    }),
    [products],
  );

  if (!role) return null;

  const status = getRoleStatus(resolveRoleStatusRecord(roleRecord, role));
  const activeProductDetails = products.find((product) => product?.product_name === activeProduct);

  const openMenu = (event) => setMenuAnchor(event.currentTarget);

  // Edit and ⋮ are icons in the side panel and the full view alike (bigger
  // in the full view), like the expand/close icons -- see
  // PanelHeaderIconButton (2026-10-01, user request).
  const editAction = !canEdit ? null : (
    <PanelHeaderIconButton expanded={expanded} title="Edit Role" onClick={() => onEdit(role, { expanded })}>
      <EditOutlined />
    </PanelHeaderIconButton>
  );

  // The ⋮ sits last, after the close control, in both modes.
  const moreActionsTrigger = (
    <PanelHeaderIconButton
      expanded={expanded}
      title="More actions"
      aria-haspopup="menu"
      aria-expanded={Boolean(menuAnchor)}
      onClick={openMenu}
    >
      <MoreVert />
    </PanelHeaderIconButton>
  );

  const moreActions = canEdit ? (
    <>
      {moreActionsTrigger}
      <Mui.Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // The expanded view is a portal at zIndex 10; keep the menu above it.
        sx={{ zIndex: 1600 }}
      >
        <Mui.MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onEdit(role, { expanded });
          }}
        >
          <Mui.ListItemIcon>
            <EditOutlined fontSize="small" />
          </Mui.ListItemIcon>
          Edit Role
        </Mui.MenuItem>
        <Mui.MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDelete(role);
          }}
          sx={{ color: "#DC2626" }}
        >
          <Mui.ListItemIcon sx={{ color: "#DC2626" }}>
            <DeleteOutline fontSize="small" />
          </Mui.ListItemIcon>
          Delete Role
        </Mui.MenuItem>
      </Mui.Menu>
    </>
  ) : null;

  const loadingBlock = <Loader info="Loading..." minHeight="40vh" />;

  const expandedHeader = (
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Mui.Typography sx={{ ...roleAndGroupUi.title, lineHeight: 1.2 }}>{role?.role_name}</Mui.Typography>
        {statusChip(status)}
      </Mui.Stack>
      {role?.description ? (
        <Mui.Typography sx={{ ...roleAndGroupUi.subtitle, mt: 0.5 }}>{role.description}</Mui.Typography>
      ) : null}
    </Mui.Box>
  );

  const collapsedBody = (
    <Mui.Stack spacing={2} sx={{ p: 2 }}>
      <Mui.Box
        sx={{
          ...roleAndGroupUi.sectionCard,
          p: 2.25,
          background: "linear-gradient(135deg, #ECFAF6 0%, #F4FBFF 100%)",
          borderColor: "#CDEDE4",
        }}
      >
        <Mui.Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <Mui.Typography sx={{ fontSize: 20, fontWeight: 700, color: designColors.slate800 }}>
            {role?.role_name}
          </Mui.Typography>
          {statusChip(status)}
        </Mui.Stack>
        {role?.description ? (
          <Mui.Typography sx={{ mt: 0.75, fontSize: 14, color: designColors.slate700 }}>
            {role.description}
          </Mui.Typography>
        ) : null}
        <Mui.Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          <Stat icon={<LayersRounded />} value={loading ? "-" : stats.modules} label="Modules" />
          <Stat icon={<GridViewRounded />} value={loading ? "-" : stats.submodules} label="Sub modules" />
          <Stat icon={<Groups2Rounded />} value={userGroups.length} label="User groups" />
        </Mui.Box>
      </Mui.Box>

      <Mui.Box sx={cardSx}>
        <SectionHeading
          icon={<ShieldRounded />}
          iconColor="#2563EB"
          iconBg="#EFF6FF"
          title="Role Details"
        />
        <Mui.Box sx={{ mt: 2 }}>
          <Mui.Typography sx={fieldLabelSx}>Role Name</Mui.Typography>
          <Mui.Box sx={readOnlyFieldSx}>{role?.role_name}</Mui.Box>
        </Mui.Box>
        <Mui.Box sx={{ mt: 1.75 }}>
          <Mui.Typography sx={fieldLabelSx}>Description</Mui.Typography>
          <Mui.Box sx={{ ...readOnlyFieldSx, minHeight: 64, color: role?.description ? designColors.slate800 : designColors.slate400 }}>
            {role?.description || "No description added."}
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <Mui.Box sx={cardSx}>
        <SectionHeading
          icon={<LayersRounded />}
          title="Modules & Sub-modules"
          subtitle="View the modules and sub-modules included in this role."
        />
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Search modules or sub-modules..."
          sx={{ mt: 1.5, width: "100%" }}
        />
        <Mui.Box sx={{ mt: 1.5 }}>
          {loading ? loadingBlock : <ModuleTree products={products} search={search} />}
        </Mui.Box>
      </Mui.Box>

      <RelatedUserGroups groups={userGroups} onOpenGroup={onOpenUserGroup} />
    </Mui.Stack>
  );

  const expandedBody = (
    <Mui.Stack spacing={2.5} sx={{ p: { xs: 2, md: 2.5 } }}>
      <Mui.Box sx={{ ...roleAndGroupUi.sectionCard, overflow: "hidden" }}>
        <Mui.Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${designColors.slate200}` }}>
          <SectionHeading
            icon={<LayersRounded />}
            title="Modules & Permissions"
            subtitle="View the modules and sub-modules included in this role and their permissions."
            action={<SearchField value={search} onChange={setSearch} placeholder="Search sub-modules..." />}
          />
        </Mui.Box>
        {loading ? (
          loadingBlock
        ) : products.length === 0 ? (
          <EmptyState title="No Modules" subtitle="This role doesn't include any modules yet." />
        ) : (
          <Mui.Grid container>
            <Mui.Grid
              item
              xs={12}
              md={4.5}
              lg={4}
              sx={{
                p: 2,
                borderRight: { md: `1px solid ${designColors.slate200}` },
                borderBottom: { xs: `1px solid ${designColors.slate200}`, md: "none" },
              }}
            >
              <Mui.Typography sx={{ ...roleKitType.sectionTitle, mb: 1.25, px: 0.5 }}>Modules</Mui.Typography>
              <ModuleList products={products} activeProduct={activeProduct} onSelect={setActiveProduct} />
            </Mui.Grid>
            <Mui.Grid item xs={12} md={7.5} lg={8} sx={{ p: 2, minWidth: 0 }}>
              {activeProductDetails ? (
                <>
                  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <ProductIcon name={activeProductDetails?.product_name} size={44} />
                    <Mui.Box>
                      <Mui.Typography sx={roleKitType.sectionTitle}>
                        Permissions for {activeProductDetails?.product_name}
                      </Mui.Typography>
                      <Mui.Typography sx={roleKitType.sectionSubtitle}>
                        These are the sub-modules and permissions included in this module.
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Stack>
                  <PermissionMatrix product={activeProductDetails} search={search} readOnly wide />
                </>
              ) : (
                <EmptyState title="No Module Selected" subtitle="Select a module to see its permissions." />
              )}
            </Mui.Grid>
          </Mui.Grid>
        )}
      </Mui.Box>
      <RelatedUserGroups groups={userGroups} onOpenGroup={onOpenUserGroup} wide />
    </Mui.Stack>
  );

  if (hidden) return null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={() => setExpanded((prev) => !prev)}
      title="View Role"
      subtitle="Review role details, modules and related user groups."
      header={expanded ? expandedHeader : undefined}
      headerActions={editAction}
      trailingActions={moreActions}
      breadcrumb={["Administration", "Roles", "View Role"]}
      bodySx={{ backgroundColor: designColors.pageBg }}
    >
      {expanded ? expandedBody : collapsedBody}
    </GlobalSidePanel>
  );
};
